import React, { useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Stepper from '../stepper/stepper';
import Footer from '../footer';
import SelectAccountBody from './select-account-body';
import { RequestAccessFormContext } from '../../contexts/request-access-form-context';
import { useGrantAccess } from '../../api/hooks/useGrantAccess';
import { isPersonalBusinessAccountSelectedButNoLocations } from './utils';

const SelectAccount = () => {
    const navigate = useNavigate();
    const {
        selectedAccounts,
        selectedLocations,
        directoryUserAccountId,
        directoryUserAccountSecret,
        setStep,
    } = useContext(RequestAccessFormContext);
    const { accessId } = useParams();
    const { mutateAsync, isPending: isGrantAccessRequestPending } = useGrantAccess();

    const handleGrantAccess = async () => {
        /* istanbul ignore next -- @preserve */
        const body = {
            accessRequestFormAccessId: accessId || '',
            directoryUserAccountId,
            directoryUserAccountSecret,
        };

        let grantAccessPromises = [];

        if (selectedAccounts.length === 0) {
            return;
        } else if (selectedLocations.length === 0) {
            grantAccessPromises = selectedAccounts.map((account) =>
                mutateAsync({ ...body, grantedAccessOverAccountId: account.id })
            );
        } else {
            grantAccessPromises = selectedLocations.map((account) =>
                mutateAsync({ ...body, grantedAccessOverLocationId: account.id })
            );
        }
        try {
            await Promise.all(grantAccessPromises);

            // if all requests succeed, navigate to access granted page
            setStep(3);
        } catch (error) {
            // if one or more of the requests fail, navigate to error page
            navigate(`/${accessId}/error`);
        }
    };

    const isSingleNonAccessGrantableAccountSelectedWithoutAnyLocations = (selectedAccounts.length === 1 && selectedAccounts[0].canGrantAccess === false && selectedLocations.length === 0)

    /**
     * Determines if the "Grant Access" button should be disabled based on the following conditions:
     * - No accounts are selected.
     * - A personal business account is selected but no locations are selected.
     * - Only one account is selected, the account cannot grant access, and no locations are selected.
     * - A grant access request is currently pending.
     *
     * @param selectedAccounts - The list of selected accounts.
     * @param selectedLocations - The list of selected locations.
     * @param isPersonalBusinessAccountSelectedButNoLocations - Function to check if a personal business account is selected but no locations are selected.
     * @param isGrantAccessRequestPending - Boolean indicating if a grant access request is pending.
     * @returns `true` if the "Grant Access" button should be disabled, `false` otherwise.
     */
    const isGrantAccessButtonDisabled =
        selectedAccounts.length === 0 ||
        isPersonalBusinessAccountSelectedButNoLocations({ selectedAccounts, selectedLocations }) ||
        isSingleNonAccessGrantableAccountSelectedWithoutAnyLocations ||
        isGrantAccessRequestPending;

    return (
        <>
            <Stepper label="app.request.access.public.step.2.navbar.label" />
            <SelectAccountBody isGrantAccessRequestPending={isGrantAccessRequestPending} />
            <Footer
                buttonLabel="app.request.access.public.step.2.submit.button"
                buttonProps={{
                    onClick: handleGrantAccess,
                    disabled: isGrantAccessButtonDisabled,
                }}
            />
        </>
    );
};

export default SelectAccount;
